import React, { useState, useEffect, useRef, memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  useTheme,
  Box,
  useMediaQuery
} from '@mui/material';
import octopusInstance from '../../services/octopus/octopusInstance';
import { getExchangeCode } from '../../services/exchanges';
import { fetchScripInfo, getLtp } from '../../services/backendAPI';
import ChartFrameModal from './ChartFrameModal';
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { addMarketWatchInstrumentAction } from "../../store/actions/watchlist";
import { openOrderWindow } from "../../store/actions/orders";

// Import new components
import TableWrapper from './TableWrapper';
import TableHeader from './TableHeader';
import OptionRow from './OptionRow';
import { 
  updateRowData, 
  findNearestStrikeIndex, 
  initializeDynamicRows 
} from './OptionDataUtils';
import styled from '@emotion/styled';
import { getProductType } from '../../utils/utilfunction';

const StyledTable = styled(Table)(({ theme }) => ({
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  border: 'none',
  
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
    padding: '6px 4px',
  textAlign: 'center',
    position: 'relative'
  },
  
  
  [theme.breakpoints.up('md')]: {
    '& .strike-cell': {
      width: '90px',
      maxWidth: '90px'
    },
    '& .ltp-cell': {
      minWidth: '120px'
    }
  },
  
  
  [theme.breakpoints.down('sm')]: {
    width: '600px', 
    minWidth: '100%',
    '& .MuiTableRow-root': {
      display: 'table-row'
  },
    '& .MuiTableCell-root': {
      display: 'table-cell',
      padding: '4px 2px',
      whiteSpace: 'nowrap'
  },
  '& .strike-cell': {
      width: '90px',
      minWidth: '90px',
      maxWidth: '90px',
      boxSizing: 'border-box'
  },
  '& .ltp-cell': {
      width: '120px',
      minWidth: '120px',
      boxSizing: 'border-box'
    }
  }
}));

const OptionTbl = memo(function OptionTbl({
  arrRows = [],
  callHeaders = [],
  putHeaders = [],
  renderCall,
  renderPut,
  underlyingToken,
  exchange,
  token,
  display_name,
  trading_symbol
}) {
  const theme = useTheme();
  const {userSettings} = useSelector((state) => state.profile);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const tableRef = useRef(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [dynamicRows, setDynamicRows] = useState([]);
  const [spotPrice, setSpotPrice] = useState(0);
  const [scripInfo, setScripInfo] = useState({});
  const wsHandlersRef = useRef([]);
  const [defaultTab, setDefaultTab] = useState('chart');
  const [switchViews, setSwitchViews] = useState(false);
  const [chartQuery, setChartQuery] = useState('');
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { watchlists, watchListIndex } = useSelector((state) => state.marketwatch);
  const [clickedCells, setClickedCells] = useState({});
  const [nearestStrikeIndex, setNearestStrikeIndex] = useState(0);
  const [initialLtpFetched, setInitialLtpFetched] = useState(false);
  const spotPriceWsHandler = useRef(null);
  const [atmIndex, setAtmIndex] = useState(0);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [currentSymbol, setCurrentSymbol] = useState('');
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    setDynamicRows(initializeDynamicRows(arrRows, exchange));
  }, [arrRows, exchange]);


  useEffect(() => {
    if (!Array.isArray(arrRows) || arrRows.length === 0) return;

    const updateRow = (index, side, msg) => {
      setDynamicRows(prev => {
        if (!prev[index]) return prev;

        const newRow = updateRowData(prev[index], side, msg);
        if (newRow === prev[index]) return prev;

        const newRows = [...prev];
        newRows[index] = newRow;
        return newRows;
      });
    };


    const handlers = arrRows.map((row, index) => {
      const ceHandler = row.call_option?.token && octopusInstance.wsHandler({
        messageType: "DetailMarketDataMessage",
        subscriptionLocation: `callOption-${row.call_option.exchange}-${row.call_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.call_option.exchange),
          instrumentToken: row.call_option.token
        }
      });

      const peHandler = row.put_option?.token && octopusInstance.wsHandler({
        messageType: "DetailMarketDataMessage",
        subscriptionLocation: `putOption-${row.put_option.exchange}-${row.put_option.token}`,
        payload: {
          exchangeCode: getExchangeCode(row.put_option.exchange),
          instrumentToken: row.put_option.token
        }
      });

      if (ceHandler) {
        ceHandler.subscribe(({ msg }) => {
          updateRow(index, 'ce', msg)
        });
      }

      if (peHandler) {
        peHandler.subscribe(({ msg }) => updateRow(index, 'pe', msg));
      }

      return { ceHandler, peHandler };
    });

    return () => {
      handlers.forEach(({ ceHandler, peHandler }) => {
        if (ceHandler?.unsubscribe) ceHandler.unsubscribe();
        if (peHandler?.unsubscribe) peHandler.unsubscribe();
      });
    };
  }, [arrRows, exchange]);


  useEffect(() => {
    if (exchange === "CDS" || exchange === "MCX") {
      fetchScripInfo(exchange, token)
        .then(({ result }) => {
          setScripInfo(result);
        });
    }
  }, [exchange, token]);

  useEffect(() => {
    if (!initialLtpFetched && underlyingToken && (
      exchange === "NSE" || exchange === "NFO" ||
      (exchange === "CDS" && scripInfo?.instrument_name?.includes("FUT")) ||
      (exchange === "MCX" && scripInfo?.instrument_name?.includes("FUT"))
    )) {
      
      (async () => {
        try {
          const newLtp = await getLtp({ exchange, token: underlyingToken });
          
          if (newLtp && newLtp.data) {
        setSpotPrice(newLtp.data);
            setInitialLtpFetched(true);
          } else {
            console.warn("Invalid LTP response:", newLtp);
            // Even if we don't get valid data, setthe flag to true so we can still set up WebSocket
            setInitialLtpFetched(true);
          }
        } catch (error) {
          console.error("Error fetching initial LTP:", error);
          // Even if there's an error, set the flag to true so we can still set up WebSocket
          setInitialLtpFetched(true);
        }
      })();
    }
  }, [exchange, underlyingToken, scripInfo, initialLtpFetched]);

  // Setup WebSocket for spot price updates
  useEffect(() => {
    // Set up WebSocket regardless of whether initial LTP is fetched
    if (underlyingToken) {
      
      // Clean up previous handler if exists
      if (spotPriceWsHandler.current) {
        spotPriceWsHandler.current.unsubscribe();
        spotPriceWsHandler.current = null;
      }

      // Create new WebSocket handler for spot price
      const handler = octopusInstance.wsHandler({
        messageType: "CompactMarketDataMessage",
        subscriptionLocation: Math.random().toString(),
        payload: {
          exchangeCode: getExchangeCode(exchange==="NFO"?"NSE":exchange),
          instrumentToken: underlyingToken
        }
      });

      // Subscribe to WebSocket updates - maintain initialScrollDone state on updates
      handler.subscribe(({ msg }) => {
        const newLtp = msg.ltp || msg.lastTradedPrice;
        if (newLtp && newLtp !== 0) {
          setSpotPrice(prev => {
            return newLtp;
          });
        }
      }).catch(error => {
        console.error("Error subscribing to spot price WebSocket:", error);
      });

      // Store the handler for cleanup
      spotPriceWsHandler.current = handler;
       
      // Cleanup function
      return () => {
        if (spotPriceWsHandler.current) {
          spotPriceWsHandler.current.unsubscribe().catch(error => {
            console.warn("Error unsubscribing from spot price WebSocket:", error);
          });
          spotPriceWsHandler.current = null;
        }
      };
    }
  }, [exchange, underlyingToken, initialScrollDone]);

  // Table centering and touch handling
  useEffect(() => {
    if (!tableRef.current) return;

    const container = tableRef.current.querySelector('.table-container');
    if (!container) return;

    // Desktop: Center the table initially
    if (!isMobile) {
    const centerTable = () => {
      const tableWidth = container.scrollWidth;
      const viewportWidth = container.offsetWidth;
      const scrollPosition = (tableWidth - viewportWidth) / 2;
      container.scrollLeft = scrollPosition;
    };

    centerTable();

      // Add resize listener to maintain centering for desktop
    const handleResize = () => {
      centerTable();
    };

    window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    } 
    // Mobile: Enable both horizontal and vertical scrolling
    else {
      // Enable both horizontal and vertical scrolling
      container.style.overflowX = 'auto';
      container.style.overflowY = 'auto';
      container.style.touchAction = 'pan-x pan-y';
      container.style.webkitOverflowScrolling = 'touch';
      
      // Ensure the table is wide enough to scroll
      const table = container.querySelector('table');
      if (table) {
        table.style.width = 'max-content';
        table.style.minWidth = '100%';
      }
      
      // Force a reflow to ensure proper scrolling
      setTimeout(() => {
        // Small horizontal scroll to ensure scrolling is active
        container.scrollLeft = 1;
        container.scrollLeft = 0;
      }, 100);
    }
  }, [isMobile]);

  // Initialize component or reset when symbols change
  useEffect(() => {
    // On component mount, initialize scrolling state
    if (!currentSymbol && trading_symbol) {
      setCurrentSymbol(trading_symbol);
      setInitialDataLoaded(false);
      setInitialScrollDone(false);
    }
    
    // Reset when trading symbol changes
    else if (trading_symbol && trading_symbol !== currentSymbol) {
      setCurrentSymbol(trading_symbol);
      setInitialDataLoaded(false);
      setInitialScrollDone(false);
    }
  }, [trading_symbol, currentSymbol]);


  // Track when data is initially loaded - only trigger scroll on first load
  useEffect(() => {
    if (dynamicRows?.length > 0 && spotPrice > 0 && !initialDataLoaded && !initialScrollDone) {
        setTimeout(() => {
        setInitialDataLoaded(true);
      }, 200);
    }
  }, [dynamicRows, spotPrice, initialDataLoaded, initialScrollDone]);

  // Force scroll on initial render once both ATM index and data are available
  useEffect(() => {
    if (initialDataLoaded && 
        atmIndex !== undefined && 
        atmIndex !== null && 
        dynamicRows?.length > 0 && 
        spotPrice > 0 && 
        !initialScrollDone && 
        tableRef.current) {
      
      setTimeout(() => {
        const tableContainer = tableRef.current.querySelector('.table-container');
        if (!tableContainer) {
          return;
        }
        
        const allRows = tableContainer.querySelectorAll('tr');
        if (!allRows.length) {
          return;
        }
        
        const atmRowIndex = atmIndex + 1;
        if (atmRowIndex >= allRows.length || atmRowIndex < 1) {
          return;
        }
        
        const atmRow = allRows[atmRowIndex];
        if (!atmRow) {
          return;
        }
        
        const containerHeight = tableContainer.clientHeight;
        const rowTop = atmRow.offsetTop;
        const rowHeight = atmRow.clientHeight;
        const scrollPosition = rowTop - (containerHeight / 2) + (rowHeight / 2);
        tableContainer.scrollTop = Math.max(0, scrollPosition);
        
        setInitialScrollDone(true);
      }, 500); 
    }
  }, [atmIndex, dynamicRows, spotPrice, initialScrollDone, initialDataLoaded, tableRef]);

  useEffect(() => {
    return () => {
      if (spotPriceWsHandler.current) {
        spotPriceWsHandler.current.close();
        spotPriceWsHandler.current = null;
      }
    };
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up WebSocket handlers
      if (spotPriceWsHandler.current) {
        spotPriceWsHandler.current.unsubscribe();
        spotPriceWsHandler.current = null;
      }
      
      // Reset all state variables related to scrolling
      setInitialScrollDone(false);
      setInitialDataLoaded(false);
      setCurrentSymbol('');
    };
  }, []);

  

  const handleLTPClick = (row, side, event) => {
    // Prevent event propagation to avoid triggering scroll
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const instrumentData = side === 'ce' ? row.ce_data : row.pe_data;
    
    // Get token from multiple possible sources with fallbacks
    const token = instrumentData?.token || row[`${side}_token`] || '';
    
    if (!instrumentData?.trading_symbol || !instrumentData?.exchange || !token) {
      enqueueSnackbar("Invalid instrument data", { variant: "error" });
      return;
    }
    
    // Set the clicked cell to show visual feedback
    const cellId = `${row.strike_price}-${side}`;
    setClickedCells(prev => ({ ...prev, [cellId]: true }));
    
    // Reset the clicked state after animation
    setTimeout(() => {
      setClickedCells(prev => ({ ...prev, [cellId]: false }));
    }, 500);
    
    // Add a small delay for mobile touch events to ensure they're not treated as scrolls
    setTimeout(() => {
      dispatch(openOrderWindow({
        type: 'BUY',
        trading_symbol: instrumentData.trading_symbol,
        display_name: instrumentData.display_name || instrumentData.trading_symbol,
        ltp: side === 'ce' ? row.ce_ltp : row.pe_ltp,
        exchange: instrumentData.exchange,
        token: token, // Use the token with fallbacks
        product: getProductType(userSettings.product_type,instrumentData.exchange),
        order_type: userSettings.order_type
      }));
    }, 100); // Increased delay for better reliability
  };

  const addToMarketWatch = async (row, side) => {
    if (watchlists[watchListIndex].instruments.length === 20) {
      enqueueSnackbar("Switch to other marketwatch to add new instrument", {
        variant: "info",
      });
      return;
    }

    const instrumentData = side === 'ce' ? row.ce_data : row.pe_data;
    
    // Get token from multiple possible sources with fallbacks
    const token = instrumentData?.token || row[`${side}_token`] || '';
    
    if (!instrumentData?.trading_symbol || !instrumentData?.exchange || !token) {
      enqueueSnackbar("Invalid instrument data", { variant: "error" });
      return;
    }
    
    let payload = {
      mw_id: watchlists[watchListIndex].id,
      exchange: instrumentData.exchange,
      trading_symbol: instrumentData.trading_symbol,
      token: `${token}`,
      execution: instrumentData.execution === 'SPREAD' ? 'SPREAD' : 'NORMAL'
    };

    const { message, status } = await dispatch(
      addMarketWatchInstrumentAction(watchListIndex, payload)
    );
    if (status === "success") {
      enqueueSnackbar(message, { variant: "success" });
    } else {
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  // Get the nearest strike index - ONLY run this on initial data load
  useEffect(() => {
    // Only calculate the nearest strike index if we haven't already scrolled
    // This prevents recalculation on every LTP change
    if (dynamicRows?.length > 0 && spotPrice > 0 && !initialScrollDone) {
      let nearestIndex = 0;
      let minDifference = Infinity;
      
      dynamicRows.forEach((row, index) => {
        const strikePrice = parseFloat(row.strike_price);
        const difference = Math.abs(strikePrice - spotPrice);
        
        if (difference < minDifference) {
          minDifference = difference;
          nearestIndex = index;
        }
      });
      
      setNearestStrikeIndex(nearestIndex);
      setAtmIndex(nearestIndex); // Also update the ATM index
    }
  }, [dynamicRows, spotPrice, initialScrollDone]);
  
  useEffect(() => {
    if (!dynamicRows || !spotPrice || initialScrollDone) return;    
   
    const sortedRows = [...dynamicRows].sort((a, b) => 
      parseFloat(a.strike_price) - parseFloat(b.strike_price)
    );
    
   
    let newAtmIndex = 0;
    for (let i = 0; i < sortedRows.length; i++) {
      const strike = parseFloat(sortedRows[i].strike_price);
      if (strike <= spotPrice && (i === sortedRows.length - 1 || parseFloat(sortedRows[i+1].strike_price) > spotPrice)) {
        // We found the strike price just below the spot price
        newAtmIndex = dynamicRows.findIndex(row => row.strike_price === sortedRows[i].strike_price);
        break;
      }
    }
    
    // If no strike price is less than the spot price, use the lowest strike
    if (newAtmIndex === -1) {
      const minStrike = Math.min(...dynamicRows.map(row => parseFloat(row.strike_price)));
      newAtmIndex = dynamicRows.findIndex(row => parseFloat(row.strike_price) === minStrike);
    }
    
    setAtmIndex(newAtmIndex);
  }, [dynamicRows, spotPrice, initialScrollDone]);

  return (
    <TableWrapper ref={tableRef}>
      <Box className="table-container">
        <StyledTable 
          size="small" 
                        sx={{
            '& .MuiTableHead-root': {
              position: 'sticky',
                            top: 0,
              zIndex: 2,
              '&::after': {
                            content: '""',
                            position: 'absolute',
                bottom: 0,
                left: 0,
                            right: 0,
                height: '1px',
                backgroundColor: theme.palette.divider
              }
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? theme.palette.grey[900] 
                : '#f5f5f5',
              fontWeight: 600,
              textAlign: 'center',
              position: 'sticky',
              top: 0
            }
          }}
        >
          {/* Table Header */}
          <TableHeader 
            callHeaders={callHeaders} 
            putHeaders={putHeaders} 
          />
          
          {/* Table Body */}
          <TableBody>
            {dynamicRows.map((row, index) => (
              <OptionRow
                key={`${row.strike_price}-${index}`}
                row={row}
                index={index}
                atmIndex={atmIndex}
                spotPrice={spotPrice}
                callHeaders={callHeaders}
                putHeaders={putHeaders}
                clickedCells={clickedCells}
                handleLTPClick={handleLTPClick}
              />
            ))}
            <TableRow>
              <TableCell 
                colSpan={
                  (isMobile 
                    ? callHeaders.filter(h => ['ltp'].includes(h.toLowerCase())).length 
                    : callHeaders.length) + 
                  1 + 
                  (isMobile 
                    ? putHeaders.filter(h => ['ltp'].includes(h.toLowerCase())).length 
                    : putHeaders.length)
                } 
                sx={{ height: '70px', padding: 0, border: 'none' }} 
              />
            </TableRow>
          </TableBody>
        </StyledTable>
      </Box>
      <ChartFrameModal
        switchViews={switchViews}
        setSwitchViews={setSwitchViews}
        url={chartQuery}
        defaultTab={defaultTab}
      />
    </TableWrapper>
  );
});

export default OptionTbl; 